import type { SharedFolderFindResponseDatagramBuf } from "~/classes/generated/response"

import { DatagramBuf } from "~/classes/generated/datagram"
import { RequestDatagramBuf, SharedFolderFindRequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseRequestId } from "~/utils/flatbuffers/datagram"
import { addEntityHandleToBuf } from "~/utils/flatbuffers/entity"
import { parseFile } from "~/utils/flatbuffers/file"
import { addSharedFolderIdToBuf, parseSharedFolderId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("sharedFolder/find", theme.colors.teal.hex)

export function sendSharedFolderFindRequests(requests: SharedFolderFindRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  const builder = new DatagramBufferBuilder(64)
  const sharedFolderRequests = []
  for (const request of requests) {
    const requestId = addRequestIdToBuf(builder, request.requestId)
    let handle
    if (request.handle) {
      handle = addEntityHandleToBuf(builder, request.handle)
    }
    const sharedFolderId = request.sharedFolderId ? addSharedFolderIdToBuf(builder, request.sharedFolderId) : undefined
    SharedFolderFindRequestDatagramBuf.startSharedFolderFindRequestDatagramBuf(builder)
    SharedFolderFindRequestDatagramBuf.addRequestId(builder, requestId)
    if (isDefined(sharedFolderId))
      SharedFolderFindRequestDatagramBuf.addSharedFolderId(builder, sharedFolderId)
    if (handle)
      SharedFolderFindRequestDatagramBuf.addHandle(builder, handle)
    SharedFolderFindRequestDatagramBuf.addOffline(builder, request.offline)
    const buf = SharedFolderFindRequestDatagramBuf.endSharedFolderFindRequestDatagramBuf(builder)
    RequestDatagramBuf.startRequestDatagramBuf(builder)
    RequestDatagramBuf.addSharedFolderFindRequest(builder, buf)
    sharedFolderRequests.push(RequestDatagramBuf.endRequestDatagramBuf(builder))
  }

  const requestBuf = DatagramBuf.createRequestVector(builder, sharedFolderRequests)
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, requestBuf)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

export function handleSharedFolderFindResponseDatagramBuf(datagram: SharedFolderFindResponseDatagramBuf) {
  onSharedFolderFindResponseDatagram({
    requestId: parseRequestId(datagram.requestId()!),
    sharedFolderId: maybe(parseSharedFolderId)(datagram.sharedFolderId()),
    home: maybe(parseFile)(datagram.home()),
    archive: maybe(parseFile)(datagram.archive()),
    error: maybe(parseDatagramError)(datagram.error()),
    offline: datagram.offline(),
  })
}

export function onSharedFolderFindResponseDatagram(response: SharedFolderFindResponseDatagram) {
  const { emitInterfaceEvent } = useEvents()
  const { emitEntityEvent } = useEvents()

  emitInterfaceEvent("sharedFolderFindResponse", response, response.requestId)

  if (response.error || !response.sharedFolderId || !response.home || !response.archive)
    return console.error("Shared folder find failed", response)

  const { home, archive } = response

  emitEntityEvent(
    "fileProperties",
    home.id,
    home,
    response.offline,
  )
  emitEntityEvent(
    "fileProperties",
    archive.id,
    archive,
    response.offline,
  )
}

export function onSharedFolderFindErrored(error: DatagramError) {
  const { notifyDatagramError } = useAlerts()
  console.error("sharedFolder find errored", error)
  notifyDatagramError(error, "Failed to find sharedFolder details")
}
